import { useEffect, useMemo, useRef } from 'react';
import { ImageComponentSlice } from '@adapters/prismic/types/brandedStorePage';
import PrismicImage from '@components/cms/PrismicImage';
import cn from 'classnames';
import CopyCard from './CopyCard';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
  ImageComponentEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import useObjectVisibility from '@hooks/useObjectVisibility';

import styles from './ImageComponent.module.scss';

const ImageComponent = ({
  primary,
  items,
  brandedStoreCoopAttribute,
  sliceIndex,
}: ImageComponentSlice) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(() => {
    return {
      page: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.ImageComponent,
      },
      ecommerce: {
        creative_name: `link,${primary?.cta_link}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.ImageComponent,
        }),
        promotion_id: primary?.title || null,
        promotion_name: primary?.cta_title || null,
        isVideo: false,
      },
    };
  }, [
    brandedStoreCoopAttribute,
    primary?.cta_link,
    primary?.cta_title,
    primary?.title,
    sliceIndex,
  ]);

  useEffect(() => {
    if (isVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...promoDetails,
      } as ImageComponentEventProps);
    }
  }, [isVisible, promoDetails]);

  const handleCTAClick = () => {
    trackPromotionEvent({
      eventName: PromotionEventName.selectPromotion,
      ...promoDetails,
    } as ImageComponentEventProps);
  };

  const copyCard = (
    <CopyCard
      copyPosition={primary?.copy_position}
      title={primary?.title}
      ctaTitle={primary?.cta_title}
      ctaLink={primary?.cta_link}
      copy={primary?.copy?.[0]?.text}
      handleCTAClick={handleCTAClick}
    />
  );

  return (
    <div className={cn(styles.sectionContainer)} id={primary?.id} ref={ref}>
      <div
        className={cn(styles.imageContainer, {
          [styles.noWrap]: primary?.no_wrap,
        })}
      >
        {primary?.copy_position === 'left' && (
          <div className={cn(styles.copyCardContainer, styles.copyCardDeskTop)}>
            {copyCard}
          </div>
        )}
        {items?.map((item, i) => (
          <>
            <div
              className={cn(styles.desktopImage, {
                [styles.aspectRaction1to1]: item?.aspect_ratio === '1 / 1',
                [styles.aspectRaction4to3]: item?.aspect_ratio === '4 / 3',
                [styles.aspectRaction16to9]: item?.aspect_ratio === '16 / 9',
              })}
              key={`desktop-image-${item?.image?.alt}-${i}`}
            >
              <PrismicImage
                src={item?.image?.url}
                alt={item?.image?.alt ?? ''}
                layout="fill"
                objectFit="contain"
              />
            </div>
            <div
              className={cn(styles.mobileImage, {
                [styles.aspectRaction1to1]: item?.aspect_ratio === '1 / 1',
                [styles.aspectRaction4to3]: item?.aspect_ratio === '4 / 3',
                [styles.aspectRaction16to9]: item?.aspect_ratio === '16 / 9',
              })}
              key={`mobile-image-${item?.mobile_image?.alt}-${i}`}
            >
              <PrismicImage
                src={item?.mobile_image?.url}
                alt={item?.mobile_image?.alt ?? ''}
                layout="fill"
                objectFit="contain"
              />
            </div>
          </>
        ))}
        {primary?.copy_position === 'right' && (
          <div className={cn(styles.copyCardContainer, styles.copyCardDeskTop)}>
            {copyCard}
          </div>
        )}
      </div>

      <div
        className={cn(styles.copyCardMobile, {
          [styles.noHide]:
            primary?.copy_position !== 'right' &&
            primary?.copy_position !== 'left',
        })}
      >
        {copyCard}
      </div>

      {primary?.caption && (
        <div className={styles.captionContainer}>
          <figcaption className={styles.imageCaption}>
            {primary?.caption}
          </figcaption>
        </div>
      )}
    </div>
  );
};

export default ImageComponent;
